import React, { useEffect, useMemo, useState } from 'react';
import {
    Department, DepartmentResponse, DepartmentTreeNodeDTO, DEPARTMENT_UNIT_TYPE_LABEL,
} from '../../interfaces/department.interface';
import { DepartmentService } from '../../services/department.service';
import { AuthService } from '../../services/auth.service';
import { Box, Button, Chip, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import GroupIcon from '@mui/icons-material/Group';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

/** A department row plus its live children, ordered by sortOrder then name (spec §2). */
interface DepartmentTreeRow extends Department {
    children: DepartmentTreeRow[];
    depth: number;
}

const ROOT_KEY = '__root__';

/**
 * Fix round 2: FALLBACK ONLY, used solely when the `GET .../tree` fetch itself fails. The primary
 * path (see `buildRowsFromTreeNodes` below) builds the displayed tree from that unpaged endpoint
 * instead of this paginated list, because the list is capped at whatever page size the caller
 * requests and its client-side nesting was never the structural source of truth. This flat-nest
 * fallback, with its orphan/cycle guards, is kept so the page still renders something (the paged
 * list, degraded to client-side nesting) rather than going blank when the tree call itself errors.
 */
const buildDepartmentTree = (departments: Department[]): DepartmentTreeRow[] => {
    const idSet = new Set(departments.map(d => d.id));
    const byParent = new Map<string, Department[]>();
    for (const dept of departments) {
        // A parentId pointing at a department not present in this list (deleted, wrong
        // institution, stale reference, etc.) would otherwise silently drop the row -- render it
        // as a root instead of losing it.
        const key = dept.parentId && idSet.has(dept.parentId) ? dept.parentId : ROOT_KEY;
        const siblings = byParent.get(key) ?? [];
        siblings.push(dept);
        byParent.set(key, siblings);
    }
    const sortSiblings = (a: Department, b: Department) =>
        (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name);

    // `visited` is shared across the whole build so a node can never appear twice; `ancestors`
    // guards a single root-to-leaf path against a parentId cycle further down that same branch.
    const visited = new Set<string>();

    const attach = (parentKey: string, depth: number, ancestors: Set<string>): DepartmentTreeRow[] =>
        (byParent.get(parentKey) ?? [])
            .slice()
            .sort(sortSiblings)
            .filter(dept => !ancestors.has(dept.id) && !visited.has(dept.id))
            .map(dept => {
                visited.add(dept.id);
                return { ...dept, depth, children: attach(dept.id, depth + 1, new Set(ancestors).add(dept.id)) };
            });

    const roots = attach(ROOT_KEY, 0, new Set());

    // A parentId cycle with no path back to an actual root (e.g. A's parent is B and B's parent
    // is A, both non-null) leaves every node in the cycle unvisited above -- surface each such
    // disjoint cycle as its own root rather than silently dropping it.
    const orphanRoots: DepartmentTreeRow[] = [];
    for (const dept of departments) {
        if (visited.has(dept.id)) continue;
        visited.add(dept.id);
        orphanRoots.push({ ...dept, depth: 0, children: attach(dept.id, 1, new Set([dept.id])) });
    }

    return [...roots, ...orphanRoots];
};

const flattenVisible = (rows: DepartmentTreeRow[], expanded: Set<string>): DepartmentTreeRow[] => {
    const result: DepartmentTreeRow[] = [];
    for (const row of rows) {
        result.push(row);
        if (row.children.length && expanded.has(row.id)) {
            result.push(...flattenVisible(row.children, expanded));
        }
    }
    return result;
};

const collectIds = (rows: DepartmentTreeRow[]): string[] =>
    rows.flatMap(row => [row.id, ...collectIds(row.children)]);

/**
 * Builds the display tree from the dedicated, UNPAGED `GET .../tree` endpoint (the source of
 * structural truth -- already ordered root->leaf by sortOrder/name, and immune to the paged flat
 * list's page-size ceiling) rather than nesting the paginated department list client-side. The
 * flat list is still fetched alongside it purely to backfill `description` (and `parentId`, needed
 * by the edit form), which `DepartmentTreeNodeDTO` doesn't carry -- merged in by id, never used to
 * drive the tree shape itself.
 */
const buildRowsFromTreeNodes = (
    nodes: DepartmentTreeNodeDTO[],
    flatById: Map<string, Department>,
    depth = 0
): DepartmentTreeRow[] =>
    nodes.map(node => {
        const match = flatById.get(node.id);
        return {
            id: node.id,
            name: node.name,
            description: match?.description ?? undefined,
            institutionId: match?.institutionId ?? '',
            unitType: node.unitType,
            parentId: match?.parentId ?? null,
            code: node.code,
            costCentreCode: match?.costCentreCode, // B4-H: keep the stored cost-centre code on edit
            sortOrder: node.sortOrder,
            depth,
            children: buildRowsFromTreeNodes(node.children, flatById, depth + 1),
        };
    });

const DepartmentList: React.FC = () => {
    const [departmentData, setDepartmentData] = useState<DepartmentResponse | null>(null);
    // The unpaged tree, when the /tree fetch succeeds. Null means "no tree available yet, or the
    // /tree call failed" -- the flat list's own parentId-nesting (with its orphan/cycle guards)
    // is kept as the fallback for that second case so the page still renders something.
    const [treeNodes, setTreeNodes] = useState<DepartmentTreeNodeDTO[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const navigate = useNavigate();
    const toast = useToast();
    const isInstitutionAdmin = usePermission(PERMISSIONS.DEPARTMENT_MANAGE);

    useEffect(() => {
        fetchDepartments();
    }, []);

    const fetchDepartments = async () => {
        try {
            const institutionId = AuthService.getSelectedInstitution() || '';
            const [flatResult, treeResult] = await Promise.allSettled([
                DepartmentService.getDepartmentsByInstitution(),
                DepartmentService.getDepartmentTree(institutionId),
            ]);
            if (flatResult.status === 'rejected') throw flatResult.reason;
            setDepartmentData(flatResult.value);
            setTreeNodes(treeResult.status === 'fulfilled' ? treeResult.value : null);

            const allIds = treeResult.status === 'fulfilled'
                ? collectIds(buildRowsFromTreeNodes(
                    treeResult.value,
                    new Map(flatResult.value.content.map(d => [d.id, d]))
                ))
                : flatResult.value.content.map(d => d.id);
            setExpanded(new Set(allIds));
        } catch (err) {
            toast.showApiError(err, 'Failed to fetch departments');
        } finally {
            setLoading(false);
        }
    };

    const flatById = useMemo(
        () => new Map((departmentData?.content ?? []).map(d => [d.id, d])),
        [departmentData]
    );
    const tree = useMemo(
        () => treeNodes
            ? buildRowsFromTreeNodes(treeNodes, flatById)
            : buildDepartmentTree(departmentData?.content ?? []),
        [treeNodes, flatById, departmentData]
    );
    const visibleRows = useMemo(() => flattenVisible(tree, expanded), [tree, expanded]);

    const toggleExpanded = (id: string) => {
        setExpanded(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const handleEdit = (department: DepartmentTreeRow) => {
        // Strip the row's tree-building fields (children/depth) before handing it to the form --
        // it only expects a plain Department.
        const { children: _children, depth: _depth, ...plainDepartment } = department;
        navigate(`/departments/edit/${department.id}`, { state: { department: plainDepartment } });
    };

    if (loading) return <Typography>Loading...</Typography>;
    if (!departmentData) return <Typography>No departments found</Typography>;

    return (
        <Box sx={{ padding: 3 }} data-help-page="departments.list.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="h5">Departments</Typography>
                {isInstitutionAdmin && (
                    <Button
                        variant="contained"
                        color="primary"
                        data-help="departments.list.add"
                        onClick={() => navigate('/departments/create')}
                    >
                        Add Department
                    </Button>
                )}
            </Box>
            <TableContainer component={Paper} data-help-section="departments.list.table">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="departments.list.col.name">Name</TableCell>
                            <TableCell data-help="departments.list.col.type">Type</TableCell>
                            <TableCell data-help="departments.list.col.code">Code</TableCell>
                            <TableCell data-help="departments.list.col.costCentre">Cost centre</TableCell>
                            <TableCell data-help="departments.list.col.description">Description</TableCell>
                            <TableCell data-help="departments.list.col.actions">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visibleRows.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} align="center">
                                    <Typography color="text.secondary">No departments found</Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {visibleRows.map((department) => (
                            <TableRow key={department.id} data-testid={`department-row-${department.id}`}>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', paddingLeft: department.depth * 3 }}>
                                        {department.children.length > 0 ? (
                                            <IconButton
                                                size="small"
                                                onClick={() => toggleExpanded(department.id)}
                                                aria-label={expanded.has(department.id) ? 'Collapse' : 'Expand'}
                                                data-help="departments.list.action.expand"
                                                data-testid={`department-toggle-${department.id}`}
                                            >
                                                {expanded.has(department.id) ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                                            </IconButton>
                                        ) : (
                                            <Box sx={{ width: 34, flexShrink: 0 }} />
                                        )}
                                        {department.name}
                                    </Box>
                                </TableCell>
                                <TableCell>
                                    <Chip size="small" label={DEPARTMENT_UNIT_TYPE_LABEL[department.unitType] ?? department.unitType} data-help="departments.list.unitType" />
                                </TableCell>
                                <TableCell>{department.code || '-'}</TableCell>
                                <TableCell data-testid={`department-cost-centre-${department.id}`}>{department.costCentreCode || '-'}</TableCell>
                                <TableCell sx={{ whiteSpace: 'normal', minWidth: 140 }}>{department.description || '-'}</TableCell>
                                <TableCell>
                                    <IconButton onClick={() => navigate(`/departments/${department.id}/users`)} color="info" title="Manage Users" aria-label="Manage Users" data-help="departments.list.action.users">
                                        <GroupIcon />
                                    </IconButton>
                                    {isInstitutionAdmin && (
                                        <IconButton onClick={() => handleEdit(department)} color="primary" title="Edit" aria-label="Edit" data-help="departments.list.action.edit">
                                            <EditIcon />
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default DepartmentList;
